import HttpFactory from '../factory'
import { AnalysisSource, AnalysisState, type MedicalAnalysis } from '../types/document'
import type { Observation } from '../types/observation'

class DocumentsModule extends HttpFactory<any[]> {
  private RESOURCE = '/documents'
  private medicalAnalysisTimeout: any = null

  async getDocumentByPatientId(patientId: string, abortSignal: AbortSignal | undefined) {
    let arr: any[] = []
    let nextPageUrl = `?subjectType=patient&subjectId=${patientId}&pageSize=100`
    do {
      await this.call<any>('GET', `${this.RESOURCE}/${nextPageUrl}`, undefined, undefined, abortSignal).then((response) => {
        arr = arr.concat(response.items)
        nextPageUrl = response.nextPageUrl
      })
    } while (nextPageUrl)
    return arr
  }

  async getDocumentDetailById(documentId: string) {
    return await this.call<any>('GET', `${this.RESOURCE}/${documentId}`)
  }

  async getDocumentLink(documentId: string) {
    return await this.call<any>('GET', `${this.RESOURCE}/${documentId}/download-link`)
  }

  async getDocumentVersion(documentId: string) {
    return await this.call<any>('GET', `${this.RESOURCE}/${documentId}/versions`)
  }

  async updateDocument(documentId: string, data: object, revision: string) {
    return await this.call<any>('PATCH', `${this.RESOURCE}/${documentId}?revision=${revision}`, data)
  }

  async postDocument(data: object) {
    return await this.call<any>('POST', `${this.RESOURCE}`, data)
  }

  async postNewVersionOfDocument(documentId: string, data: object) {
    return await this.call<any>('POST', `${this.RESOURCE}/${documentId}/versions`, data)
  }

  async postDocumentObservation(documentId: string, data: object) {
    return await this.call<any>('POST', `${this.RESOURCE}/${documentId}/observations`, data)
  }

  async deleteDocument(documentId: string) {
    return await this.call<any>('DELETE', `${this.RESOURCE}/${documentId}`)
  }

  /**
   * IA
   */
  async openMedicalAnalysis(documentId: string, revision: string): Promise<void> {
    return await this.call<any>(
      'PATCH',
      `${this.RESOURCE}/${documentId}/medical-analyses/data`,
      {
        state: AnalysisState.available,
      },
      { params: { revision } },
    )
  }

  async rejectAISuggestion(documentId: string, suggestionId: string): Promise<void> {
    return await this.call<any>(
      'PATCH',
      `${this.RESOURCE}/${documentId}/medical-analyses/data/suggested-observations/${suggestionId}`,
      {
        status: 'rejected',
      },
    )
  }

  async rejectAISuggestionList(documentId: string, suggestionIds: string[]): Promise<void> {
    let promises: any = []
    suggestionIds.forEach(async (id) => {
      promises.push(this.rejectAISuggestion(documentId, id))
    })

    await Promise.all(promises)
  }

  async closeMedicalAnalysis(documentId: string, revision: string): Promise<void> {
    return await this.call<any>(
      'PATCH',
      `${this.RESOURCE}/${documentId}/medical-analyses/data`,
      {
        state: AnalysisState.closed,
      },
      { params: { revision } },
    )
  }

  async getMedicalAnalysis(documentId: string): Promise<MedicalAnalysis> {
    const medicalAResult = new Promise<MedicalAnalysis>((resolve, reject) => {
      if (this.medicalAnalysisTimeout) {
        clearTimeout(this.medicalAnalysisTimeout)
        this.medicalAnalysisTimeout = null
      }
      this.medicalAnalysisTimeout = setTimeout(() => {
        resolve({
          documentId: 'documentId',
          revision: 'revisionId',
          documentVersion: 1,
          source: AnalysisSource.data,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 101,
          closedBy: 'closedById',
          state: AnalysisState.available,
          rawAnalysis: null,
        } as MedicalAnalysis)
      }, 300)
    })

    //return medicalAResult
    return await this.call<any>('GET', `${this.RESOURCE}/${documentId}/medical-analyses/data`)
  }

  async triggerMedicalAnalysis(documentId: string): Promise<MedicalAnalysis> {
    return await this.call<any>('POST', `${this.RESOURCE}/${documentId}/medical-analyses/data`)
  }

  async getMedicalAnalysisSuggestions(documentId: string): Promise<Observation[]> {
    /**return [
      {
        code: 'bleeding-major-risk_has-bled',
        device: null,
        effectiveAt: '2024-10-09T10:25:00.000Z',
        id: 'bf2270c8-6820-4139-a550-2535691d7984',
        performer: {
          id: '4e5b533c-3a77-47ce-a8ff-b9cce1dc8fbf',
          type: 'practitioner',
        },
        value: {
          valueInteger: 9,
        },
      },
      {
        code: 'body-temperature',
        device: null,
        effectiveAt: '2024-10-09T10:25:00.000Z',
        id: '5c8e04e8-4dff-488e-9fbe-18fd556c6c5f',
        performer: {
          id: '4e5b533c-3a77-47ce-a8ff-b9cce1dc8fbf',
          type: 'practitioner',
        },
        value: {
          valueFloat: 10,
        },
      },
    ]*/

    return await this.call<any>('GET', `${this.RESOURCE}/${documentId}/medical-analyses/data/suggested-observations`)
  }
}

export default DocumentsModule
